import type { Session } from 'next-auth';

import { getCmsOffers, getDownloadConfiguration } from '@services/cms.service';
import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { logger } from '@services/loggers';
import { fetchProductsOptions } from '@services/options.service';

import { ApiCmsOfferType, ApiProductDetailed, ApiProductType } from '@app-types/api';
import type { ContentEditorItem } from '@app-types/cms';
import { type FaiProduct, type FaiType, RangesNewGeneration } from '@app-types/fai';
import { PlanType, Product, RepeaterProduct } from '@app-types/product';

import { FAI_BYOU_CATEGORY, MAIN_OFFERS_CATEGORY, callSapicApi, createRepeaterCall, getPayload } from '@helpers/sapic';

import { API_X_SOURCE } from '@constants/api';
import { PlanTypeId } from '@constants/plan';

import { SapiMapper } from 'src/mappers/sapi-mapper';
import { sapiRepository } from 'src/repositories/sapi';

import { SapicMapper } from '../mappers/sapic-mapper';
import { CACHE_TAGS } from './http/http';

export async function getFaiProductsFromSapic(session: Session | null): Promise<FaiType[]> {
    const initialPayload = getPayload(session, undefined, true);
    const response = await callSapicApi(
        initialPayload,
        session,
        FAI_BYOU_CATEGORY,
        undefined,
        {},
        API_X_SOURCE,
        SapicMapper.mapToFai,
    );
    return response;
}

export async function getFaiProductsOrderCms(): Promise<string[]> {
    try {
        const configCE = await getDownloadConfiguration();
        if (!configCE?.offres_fai) {
            return [];
        }
        return configCE.offres_fai.flatMap((o) => {
            const id = o.offre.split('/')[1];
            return id ? [id] : [];
        });
    } catch (e) {
        logger.error('Error fetch fai config', e);
        return [];
    }
}

export async function getPlans(category: string, session: Session | null, faiGencode?: string) {
    const payload = getPayload(session, faiGencode);
    return await callSapicApi(
        payload,
        session,
        category,
        `type in ('${PlanTypeId.SOWO}','${PlanTypeId.SOWO_SPECIALS}')`,
    );
}

export async function fetchProductsAndConf(faiProducts: FaiProduct[] = [], session: Session | null = null) {
    // Helper to find product gencode by range and series
    const getFaiProductGencode = (rangeNg: RangesNewGeneration, commercialSeries: string) =>
        faiProducts.find((product) => product.range_ng === rangeNg && product.commercial_series === commercialSeries)
            ?.gencode_offre;

    const pureFibreMaxOfferGencode = getFaiProductGencode(RangesNewGeneration.VERY_FIT, 'max');
    const pureFibreStandardOfferGencode = getFaiProductGencode(RangesNewGeneration.VERY_FIT, 'standard');
    const bboxPlusTvOfferGencode = getFaiProductGencode(RangesNewGeneration.MUST, 'serie_client_bandyou');

    const offersWithRepeaters = faiProducts
        .filter((product) => product.gencode_repeater)
        .map((product) => ({
            offerGencode: product.gencode_offre,
            repeaterGencode: product.gencode_repeater,
        }));

    const repeaterCalls = offersWithRepeaters.map(({ offerGencode, repeaterGencode }) =>
        createRepeaterCall(offerGencode, repeaterGencode, faiProducts, session),
    );

    const [plansResult, pureFibreResultMax, pureFibreResultStandard, bboxPlusTvResult, ...repeaterResults] =
        await Promise.allSettled([
            getPlans(MAIN_OFFERS_CATEGORY, session),
            pureFibreMaxOfferGencode
                ? getPlans(MAIN_OFFERS_CATEGORY, session, pureFibreMaxOfferGencode)
                : Promise.resolve([]),
            pureFibreStandardOfferGencode
                ? getPlans(MAIN_OFFERS_CATEGORY, session, pureFibreStandardOfferGencode)
                : Promise.resolve([]),
            bboxPlusTvOfferGencode
                ? getPlans(MAIN_OFFERS_CATEGORY, session, bboxPlusTvOfferGencode)
                : Promise.resolve([]),
            ...repeaterCalls,
        ]);

    const extractResult = <T>(result: PromiseSettledResult<T[]>): T[] =>
        result.status === 'fulfilled' ? result.value : [];

    const plans = extractResult(plansResult);
    const plansWithPureFibreMaxInBody = extractResult(pureFibreResultMax);
    const plansWithPureFibreStandardInBody = extractResult(pureFibreResultStandard);
    const plansWithBboxPlusTvInBody = extractResult(bboxPlusTvResult);

    const repeaters: { [key: string]: RepeaterProduct } = {};

    repeaterResults.forEach((result, index) => {
        const offerData = offersWithRepeaters[index];
        if (offerData && result.status === 'fulfilled' && result.value?.[0]) {
            repeaters[offerData.offerGencode] = result.value[0];
        }
    });

    const plansWithIncitationBundlePromo = addBboxDiscountedPricesToPlans(
        plans,
        plansWithPureFibreMaxInBody,
        plansWithPureFibreStandardInBody,
        plansWithBboxPlusTvInBody,
    );

    const productsWithDetails = await fetchProductsOptions(plansWithIncitationBundlePromo);

    const cmsOffers = await getCmsOffers();

    return mergeProductsData(productsWithDetails, cmsOffers, { repeaters });
}

export async function fetchProductByGencode(gencodes: string[]): Promise<Product[]> {
    if (!gencodes) {
        throw new Error('[Fetch Product] Missing gencodes parameters');
    }
    const listGencode = gencodes.join(',');
    const data = await apiHttpService.get<{
        produits: { [key: string]: ApiProductType };
    }>(`${getAppConfig().bytelApi.url}/ventes/produits?listGencode=${listGencode}`, undefined, {
        ttl: parseInt(getAppConfig().cache.ttl.products),
        tags: [CACHE_TAGS.PRODUCTS],
    });

    if (!data) {
        throw new Error('Products not found');
    }

    const products = Object.values(data.produits)?.map((product) => SapiMapper.mapToProduct(product));
    return products;
}

export async function fetchDetailedProductByGencode(gencode: string) {
    if (!gencode) {
        throw new Error('[Fetch Product] Missing gencodes parameters');
    }
    const product = await apiHttpService.get<ApiProductDetailed>(
        `${getAppConfig().bytelApi.url}/ventes/produits/${gencode}`,
        undefined,
        {
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        },
    );

    if (!product) {
        throw new Error('Products not found');
    }

    return product;
}

export async function fetchProductByUrlKey(urlKey: string): Promise<Product | null> {
    if (!urlKey) {
        return null;
    }

    try {
        const data = await sapiRepository.getProductByUrl<ApiProductType>(urlKey);
        if (!data) {
            return null;
        }

        const product = SapiMapper.mapToProduct(data);
        return product;
    } catch (error) {
        console.log('Error fetching product by url key:', error);
        return null;
    }
}

function mergeProductsData(
    planList: PlanType[],
    offers: ApiCmsOfferType[] | null,
    faiAdditionalProducts?: {
        repeaters: { [key: string]: RepeaterProduct };
    },
) {
    if (!planList) {
        logger.log('[ERROR][fetch planList failed]');
        throw new Error('failed');
    }

    if (Object.keys(planList).length === 0) {
        logger.error('[ERROR][analyzeProducts] failed, planList in entry is empty]');
        throw new Error('failed');
    }

    const completePlans = planList.map((plan) => {
        const productDirectUsInfos = getProductCmsData(plan.gencode, offers);
        return completePlanData(plan, productDirectUsInfos);
    });

    return {
        plans: completePlans,
        faiAdditionalProducts,
    };
}

const getProductCmsData = (gencode: string, mcoOffers: ApiCmsOfferType[] | null): ApiCmsOfferType => {
    if (!mcoOffers) return {} as ApiCmsOfferType;
    return mcoOffers.find((pr) => pr.gencode === gencode) || ({} as ApiCmsOfferType);
};

const completePlanData = (product: PlanType, productDirectUsInfos: ApiCmsOfferType): PlanType => {
    return {
        ...product,
        ...productDirectUsInfos,
        bannerText: productDirectUsInfos.bannerText ?? undefined,
        networkText: productDirectUsInfos.networkText ?? 'Qualité du réseau Bouygues Telecom',
        radioFlag: productDirectUsInfos.radio_flag ?? undefined,
        radioFlagColor: productDirectUsInfos.radio_flag_color ?? undefined,
        addedPepites:
            productDirectUsInfos.add_pepites?.map((pepite) => ({
                icon: pepite.icon_trilogy ?? undefined,
                pepite: pepite.pepite ?? undefined,
            })) ?? [],
        textLinkPopin: productDirectUsInfos.text_lien_popin ?? undefined,
        roamingDestinations: productDirectUsInfos.roaming_destinations ?? undefined,
        imagesRoaming: productDirectUsInfos.add_img_roaming?.items ?? undefined,
        planPicto: productDirectUsInfos.picto?.items ?? undefined,
        roamingPopinTitle: productDirectUsInfos.title_popin ?? undefined,
        stickerPosition: productDirectUsInfos.sticker_position ?? 0,
        isHighlighted: productDirectUsInfos.highLight,
        animco: {
            startDateTimer: productDirectUsInfos.start_date_timer ?? undefined,
            endDateTimer: productDirectUsInfos.end_date_timer ?? undefined,
            sticker: productDirectUsInfos.sticker ?? undefined,
            promoOffer: productDirectUsInfos.promo_offer ?? undefined,
            divider: productDirectUsInfos.divider ?? false,
        },
    };
};

const addBboxDiscountedPricesToPlans = (
    plans: PlanType[],
    plansWithPureFibreMaxInBody: PlanType[],
    plansWithPureFibreStandardInBody: PlanType[],
    plansWithBboxPlusTvInBody: PlanType[],
) => {
    const findPlanByGencode = (plansList: PlanType[], gencode: string) => plansList.find((p) => p.gencode === gencode);

    return plans.map((plan) => {
        const discountedPlans = {
            max: findPlanByGencode(plansWithPureFibreMaxInBody, plan.gencode),
            standard: findPlanByGencode(plansWithPureFibreStandardInBody, plan.gencode),
            serie_client_bandyou: findPlanByGencode(plansWithBboxPlusTvInBody, plan.gencode),
        };

        const hasPriceChanges = Object.values(discountedPlans).some(
            (discountedPlan) => discountedPlan?.prices.final !== plan.prices.final,
        );

        if (hasPriceChanges) {
            return {
                ...plan,
                prices: {
                    ...plan.prices,
                    pricesWithBboxIncentive: {
                        max: discountedPlans.max?.prices,
                        standard: discountedPlans.standard?.prices,
                        serie_client_bandyou: discountedPlans.serie_client_bandyou?.prices,
                    },
                },
            };
        }
        return plan;
    });
};

export const mergeCeAndSapicData = (
    ceData: ContentEditorItem[],
    sapicData: FaiType[],
    faiOrder: string[],
): FaiProduct[] => {
    const results: FaiProduct[] = [];
    for (const ceItem of ceData) {
        const matchingProduct = sapicData.find((product) => product.sku === ceItem.gencode_offre);
        if (matchingProduct) {
            results.push({
                ...ceItem,
                ...matchingProduct,
                animco: {
                    sticker: ceItem.sticker ?? undefined,
                    startDateTimer: ceItem.start_date_timer ?? undefined,
                    endDateTimer: ceItem.end_date_timer ?? undefined,
                    promoOffer: ceItem.promo_offer ?? undefined,
                },
            });
        }
    }

    return sortByFaiOrder(results, faiOrder);
};

const sortByFaiOrder = (faiProducts: FaiProduct[], faiOrder: string[]): FaiProduct[] => {
    return faiProducts.sort((a, b) => {
        const indexA = faiOrder.indexOf(a.id.toString());
        const indexB = faiOrder.indexOf(b.id.toString());
        return indexA - indexB;
    });
};
